import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Generic add-to-cart custom element for GURMEO editorial surfaces.
 *
 * <gurmeo-duo-cart> — the original "Das Duo" element
 *   (sections/gurmeo-house-duo-v2.liquid: two bottles, one submit).
 * <gurmeo-cart-form> — the generic alias for any items[i][id|quantity] form
 *   (e.g. the Kollektion card quick-add in sections/gurmeo-house-row.liquid).
 *   Set data-source on the element to attribute the cart event.
 *
 * Without this (or without JS) the inner <form> posts natively to /cart/add and
 * the browser navigates to the /cart page. With it, the add happens over fetch
 * and — mirroring product-form-component's contract exactly — dispatches a
 * CartLinesUpdateEvent carrying the re-rendered cart sections, so <cart-drawer>
 * refreshes and auto-opens on the same page instead of navigating away.
 */
class GurmeoDuoCart extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;

  connectedCallback() {
    this.#form = this.querySelector('form');
    this.#form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#onSubmit);
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    // Fall back to the native POST (→ /cart) if the theme runtime isn't ready.
    if (typeof Theme === 'undefined' || !Theme.routes?.cart_add_url) return;

    event.preventDefault();

    const form = this.#form;
    if (!form) return;
    const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'));
    if (button?.disabled) return;

    const formData = new FormData(form);

    // Ask /cart/add to also return the re-rendered cart-items sections so the
    // drawer can morph to fresh content (same mechanism as product-form).
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    // Optimistic lines from the items[i][id|quantity] fields.
    const lines = [];
    for (let i = 0; ; i += 1) {
      const id = formData.get(`items[${i}][id]`);
      if (id == null) break;
      lines.push({ merchandiseId: String(id), quantity: Number(formData.get(`items[${i}][quantity]`)) || 1 });
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise: deferred.promise,
      })
    );

    if (button) {
      button.disabled = true;
      // Lights the canonical pill spinner (gurmeo-buttons.css [aria-busy]).
      button.setAttribute('aria-busy', 'true');
    }

    const cfg = fetchConfig('javascript', { body: formData });
    const cartUrl = Theme.routes.cart_url || '/cart';

    fetch(Theme.routes.cart_add_url, {
      ...cfg,
      headers: { ...cfg.headers, Accept: 'text/html' },
    })
      .then((response) => response.json())
      .then((response) => {
        // Server explicitly REJECTED the add (e.g. an item is sold out): nothing
        // was added, so a native re-POST is safe and lands on /cart with
        // Shopify's own error handling.
        if (response.status) {
          deferred.reject(new Error(response.message || 'Add to cart failed'));
          form.submit();
          return undefined;
        }

        // Add SUCCEEDED — refetch the cart to resolve the drawer event. A failure
        // here must NEVER re-POST (the items are already in the cart, re-POSTing
        // would double-add); navigate to the cart page so the customer sees the
        // real state instead.
        return fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((ajaxCart) => {
            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
              detail: {
                items: ajaxCart.items,
                source: this.dataset.source || 'gurmeo-house-duo',
                sourceId: this.id || this.dataset.source || 'gurmeo-house-duo',
                itemCount: lines.reduce((total, line) => total + line.quantity, 0),
                sections: response.sections,
                didError: false,
              },
            });
          })
          .catch((error) => {
            deferred.reject(error);
            window.location.assign(cartUrl);
          });
      })
      .catch((error) => {
        // The add request itself failed (network/parse). The mutation MAY have
        // committed, so do NOT re-POST — just reject and surface a visible error
        // (mirroring product-form-component) so the shopper sees the failure
        // instead of silence, then let the button re-enable to retry. Without
        // this, a committed-but-lost-response looks like "nothing happened" and
        // invites a re-tap that double-adds.
        deferred.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      });
  };
}

if (!customElements.get('gurmeo-duo-cart')) {
  customElements.define('gurmeo-duo-cart', GurmeoDuoCart);
}
// Generic alias — a constructor may only be registered once, so subclass.
if (!customElements.get('gurmeo-cart-form')) {
  customElements.define('gurmeo-cart-form', class extends GurmeoDuoCart {});
}
